import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The few things every hand-drawn chart here needs: its own width, ring geometry, a readable text
 * colour on any fill, and a tooltip. Charts draw at their real pixel size (not a scaled viewBox),
 * so the text is the same size on a phone as on a wide screen.
 */

/** [ref, width] — the width of the element `ref` is put on, kept up to date. 0 until measured. */
export function useWidth() {
  const ref = useRef(null)
  const [w, setW] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, w]
}

export const TAU = Math.PI * 2

/** The point at angle `a` (radians, clockwise from twelve o'clock) and radius `r`. */
export const polar = (cx, cy, r, a) => [cx + r * Math.sin(a), cy - r * Math.cos(a)]

/** A piece of a ring: angles a0..a1, radii r0..r1 (r0 = 0 gives a pie slice). */
export function sector(cx, cy, r0, r1, a0, a1) {
  if (a1 - a0 >= TAU - 1e-6) a1 = a0 + TAU - 1e-4   // a full ring cannot be one arc
  const large = a1 - a0 > Math.PI ? 1 : 0
  const [x0, y0] = polar(cx, cy, r1, a0), [x1, y1] = polar(cx, cy, r1, a1)
  if (r0 <= 0.5) return `M${cx},${cy}L${x0},${y0}A${r1},${r1} 0 ${large} 1 ${x1},${y1}Z`
  const [x2, y2] = polar(cx, cy, r0, a1), [x3, y3] = polar(cx, cy, r0, a0)
  return `M${x0},${y0}A${r1},${r1} 0 ${large} 1 ${x1},${y1}L${x2},${y2}A${r0},${r0} 0 ${large} 0 ${x3},${y3}Z`
}

/** An arc for text to follow along radius r, from a0 to a1. On the lower half it is drawn the
 *  other way round so the words read left to right instead of upside down; `inward` then says the
 *  letters stand on the inside of the line, so the caller can move it out by a line's height. */
export function textArc(cx, cy, r, a0, a1) {
  const inward = Math.cos((a0 + a1) / 2) < 0
  const [sx, sy] = polar(cx, cy, r, inward ? a1 : a0)
  const [ex, ey] = polar(cx, cy, r, inward ? a0 : a1)
  const large = a1 - a0 > Math.PI ? 1 : 0
  return { d: `M${sx},${sy}A${r},${r} 0 ${large} ${inward ? 0 : 1} ${ex},${ey}`, inward }
}

/** About how wide a line of text is, in px. Close enough to decide whether a label fits. */
export const textW = (s, size = 12, bold = false) => String(s ?? '').length * size * (bold ? 0.6 : 0.55)

/** Text cut to about `px` wide, with an ellipsis if it had to be cut. */
export function fitText(s, px, size = 12, bold = false) {
  const t = String(s ?? '')
  if (textW(t, size, bold) <= px) return t
  const n = Math.max(1, Math.floor(px / (size * (bold ? 0.6 : 0.55))) - 1)
  return n >= t.length ? t : `${t.slice(0, n)}…`
}

const lin = (c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4) }
const rgb = (hex) => { const h = hex.replace('#', ''); return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16)) }
const lum = (hex) => { const [r, g, b] = rgb(hex).map(lin); return 0.2126 * r + 0.7152 * g + 0.0722 * b }

/** White or near-black, whichever reads better on this fill. */
export const inkOn = (hex) => (1.05 / (lum(hex) + 0.05) >= (lum(hex) + 0.05) / 0.0603 ? '#ffffff' : '#1A1A1A')

/** A colour moved a fraction `t` (0..1) of the way to white — a lighter shade of the same hue. */
export function tint(hex, t) {
  const c = rgb(hex).map(v => Math.round(v + (255 - v) * t))
  return `#${c.map(v => v.toString(16).padStart(2, '0')).join('')}`
}

/** A small seeded random generator, so a layout that uses chance comes out the same every time. */
export function seeded(text) {
  let a = 2166136261
  for (let i = 0; i < text.length; i++) a = Math.imul(a ^ text.charCodeAt(i), 16777619)
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Labels on one side of a chart, moved apart so none overlaps the next: each wants to sit at
 *  `y`, they keep at least `gap` between them, and stay inside lo..hi. Returns new y's in order. */
export function spread(wanted, gap, lo, hi) {
  const order = wanted.map((y, i) => [y, i]).sort((a, b) => a[0] - b[0])
  const ys = order.map(([y]) => Math.max(lo, Math.min(hi, y)))
  for (let i = 1; i < ys.length; i++) ys[i] = Math.max(ys[i], ys[i - 1] + gap)
  if (ys.length && ys[ys.length - 1] > hi) {
    ys[ys.length - 1] = hi
    for (let i = ys.length - 2; i >= 0; i--) ys[i] = Math.min(ys[i], ys[i + 1] - gap)
  }
  const out = []
  order.forEach(([, i], k) => { out[i] = ys[k] })
  return out
}

/** A tooltip for a hand-drawn chart. Put `ref` on the chart's wrapper (position: relative), call
 *  `show(event, content)` from a mark's pointer or focus handlers and `hide()` on leave, and render
 *  <ChartTip state={tip.state} /> inside the wrapper. */
export function useTip() {
  const ref = useRef(null)
  const [state, setState] = useState(null)
  const show = useCallback((e, content) => {
    const box = ref.current?.getBoundingClientRect()
    if (!box) return
    let x, y
    if (e?.clientX) { x = e.clientX - box.left; y = e.clientY - box.top }
    else {
      const r = e?.currentTarget?.getBoundingClientRect?.()
      x = r ? r.left + r.width / 2 - box.left : box.width / 2
      y = r ? r.top - box.top : 0
    }
    setState({ x, y, w: box.width, content })
  }, [])
  const hide = useCallback(() => setState(null), [])
  return { ref, show, hide, state }
}
